import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { X, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { MESSAGEABLE_USERS_QUERY } from '~/graphql/connection';
import { useAnalytics } from '~/context/analytics';
import { UserAvatar } from '../UserAvatar';
import { shouldShowDmRecipientEmptyState, shouldShowDmRecipientMenu } from '@arthelper/shared/dm-recipient-picker';

export interface RecipientUser {
  asfId: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  slug?: string | null;
  avatarUrl?: string | null;
}

interface Props {
  value: RecipientUser[];
  onChange: (users: RecipientUser[]) => void;
  max?: number;
  placeholder?: string;
}

function useDebounce(value: string, ms: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function displayNameOf(u: RecipientUser, fallback: string) {
  return u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.slug || fallback;
}

export function RecipientPicker({ value, onChange, max = 30, placeholder }: Props) {
  const { t } = useTranslation('social');
  const {
    amplitude: { track },
  } = useAnalytics();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const debouncedSearch = useDebounce(search.trim(), 250);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, loading } = useQuery(MESSAGEABLE_USERS_QUERY, {
    variables: { query: debouncedSearch, first: 20 },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    function handleClick(ev: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selectedIds = new Set(value.map((u) => u.asfId));
  const results: RecipientUser[] = (data?.messageableUsers?.edges || [])
    .map((e: any) => e?.node)
    .filter((u: RecipientUser | undefined) => u && !selectedIds.has(u.asfId));

  const canAddMore = value.length < max;
  const showEmptyMessage = shouldShowDmRecipientEmptyState({
    loading,
    remainingCount: results.length,
    hasSearch: !!debouncedSearch,
    selectedCount: value.length,
  });
  const showMenu = shouldShowDmRecipientMenu({
    open,
    loading,
    remainingCount: results.length,
    showEmptyMessage,
  });

  function addUser(user: RecipientUser) {
    if (selectedIds.has(user.asfId) || !canAddMore) return;
    const next = [...value, user];
    onChange(next);
    track('dm_recipient_added', { recipient_count_after: next.length });
    setSearch('');
  }

  function removeUser(asfId: string) {
    const next = value.filter((u) => u.asfId !== asfId);
    onChange(next);
    track('dm_recipient_removed', { recipient_count_after: next.length });
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        className="min-h-[44px] w-full border border-gray-200 rounded-lg bg-white px-3 py-2 flex flex-wrap gap-1.5 items-center focus-within:ring-2 focus-within:ring-gray-900 focus-within:ring-offset-0 focus-within:border-gray-900"
        onClick={() => setOpen(true)}
      >
        {value.map((u) => (
          <span
            key={u.asfId}
            className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-800 text-sm rounded-full pl-1 pr-2 py-0.5"
          >
            <UserAvatar
              avatarUrl={u.avatarUrl}
              firstName={u.firstName}
              lastName={u.lastName}
              displayName={u.displayName}
              size={20}
            />
            <span>{displayNameOf(u, t('network.unnamed-user', 'Unnamed'))}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeUser(u.asfId);
              }}
              className="text-gray-500 hover:text-gray-800"
              aria-label={t('network.remove-recipient', 'Remove recipient')}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <div className="flex-1 flex items-center min-w-[120px]">
          {value.length === 0 && <Search className="w-4 h-4 text-gray-400 mr-1.5" />}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={
              canAddMore
                ? placeholder || t('dm.recipient-placeholder', 'Search your connections')
                : t('dm.recipient-max', 'Max {{max}} recipients', { max })
            }
            disabled={!canAddMore}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-[80px]"
          />
        </div>
      </div>

      {showMenu && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto z-20">
          {loading && (
            <div className="py-6 flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900" />
            </div>
          )}
          {showEmptyMessage && (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">
              {debouncedSearch
                ? t('dm.no-recipient-match', 'No connections match your search')
                : t('dm.no-connections', 'You have no connections yet')}
            </div>
          )}
          {!loading &&
            results.map((user) => (
              <button
                key={user.asfId}
                type="button"
                onClick={() => addUser(user)}
                className="flex items-center gap-3 w-full px-3 py-2 hover:bg-gray-50 text-left"
                disabled={!canAddMore}
              >
                <UserAvatar
                  avatarUrl={user.avatarUrl}
                  firstName={user.firstName}
                  lastName={user.lastName}
                  displayName={user.displayName}
                  size={32}
                />
                <span className="text-sm text-gray-900">
                  {displayNameOf(user, t('network.unnamed-user', 'Unnamed'))}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
